import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { Download, FileSignature, FileText, Plus, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import type { Employee } from '@/types';
import { documentsRhApi, LIBELLE_DOCUMENT, type DocumentRh, type TypeDocumentRh } from '@/api/documentsRh';
import { ContratDialog } from './ContratDialog';

/**
 * PAIE-1 — documents RH d'un employé (docs/PLAN-PAIE.md) : attestation de travail (employé en poste), certificat de
 * travail (employé sorti). Numérotés, contenu figé à l'émission, réimprimés à l'identique ; annulation motivée.
 */
const dateCourte = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

export const DocumentsEmploye = ({ employee }: { employee: Employee }) => {
  const role = useAuthStore((s) => s.user?.role);
  const peutEmettre = role === 'rh' || role === 'daf' || role === 'admin_holding';
  const qc = useQueryClient();
  const [contrat, setContrat] = useState(false);
  const cle = ['hr', 'documents', employee.id];
  const q = useQuery({ queryKey: cle, queryFn: () => documentsRhApi.lister(employee.id) });
  const sorti = employee.status === 'terminated' || (!!employee.endDate && employee.endDate.slice(0, 10) < new Date().toISOString().slice(0, 10));

  const telecharger = (d: { id: string; numero: string }) =>
    documentsRhApi.telecharger(d.id, d.numero).catch(() => toast.error('PDF indisponible', 'Réessayez dans un instant.'));
  const emettre = useMutation({
    mutationFn: (type: TypeDocumentRh) => documentsRhApi.emettre(employee.id, type),
    onSuccess: (d) => {
      toast.success('Document émis', d.numero);
      void qc.invalidateQueries({ queryKey: cle });
      void telecharger(d);
    },
  });
  const annuler = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => documentsRhApi.annuler(id, motif),
    onSuccess: () => { toast.success('Document annulé'); void qc.invalidateQueries({ queryKey: cle }); },
  });
  const demanderAnnulation = async (d: DocumentRh) => {
    const motif = await demander.texte({ titre: `Annuler ${d.numero} ?`, libelle: 'Motif (5 caractères au moins)', min: 5, confirmer: 'Annuler le document', danger: true });
    if (motif === null) return;
    annuler.mutate({ id: d.id, motif });
  };

  return (
    <div className="ds-dp-section">
      <div className="ds-dp-section-title">Documents</div>
      {peutEmettre && (
        <ContratDialog open={contrat} onClose={() => setContrat(false)} employee={employee} onEmis={(d) => {
          toast.success('Contrat émis', d.numero);
          void qc.invalidateQueries({ queryKey: cle });
          void telecharger(d);
        }} />
      )}
      {peutEmettre && (
        <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
          {!sorti ? (
            <button className="ds-btn ds-btn-p ds-btn-sm" style={{ justifyContent: 'center' }} disabled={emettre.isPending}
              onClick={() => emettre.mutate('attestation_travail')}>
              <Plus size={13} /> Attestation de travail
            </button>
          ) : (
            <button className="ds-btn ds-btn-p ds-btn-sm" style={{ justifyContent: 'center' }} disabled={emettre.isPending || !employee.endDate}
              title={!employee.endDate ? 'Renseignez d’abord la date de sortie (Modifier la fiche)' : ''}
              onClick={() => emettre.mutate('certificat_travail')}>
              <Plus size={13} /> Certificat de travail
            </button>
          )}
          {!sorti && (
            <button className="ds-btn ds-btn-g ds-btn-sm" style={{ justifyContent: 'center' }} onClick={() => setContrat(true)}>
              <FileSignature size={13} /> Contrat de travail
            </button>
          )}
          {sorti && !employee.endDate && (
            <div style={{ fontSize: 11, color: 'var(--warning)' }}>Date de sortie à renseigner (Modifier la fiche) avant le certificat.</div>
          )}
        </div>
      )}
      {q.isLoading ? <div style={{ fontSize: 12 }}>Chargement…</div> : (q.data?.length ?? 0) === 0 ? (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <FileText size={24} style={{ margin: '0 auto 6px', color: 'var(--tx-3)' }} />
          <p style={{ fontSize: 12, color: 'var(--tx-3)' }}>Aucun document émis</p>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {q.data!.map((d) => (
            <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, opacity: d.annuleLe ? 0.55 : 1 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{LIBELLE_DOCUMENT[d.type]} · {d.numero}</div>
                <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                  {dateCourte(d.emisLe)}{d.emisPar ? ` · ${d.emisPar}` : ''}{d.annuleLe ? ` · annulé : ${d.motifAnnulation}` : ''}
                </div>
              </div>
              <button className="ds-btn ds-btn-g ds-btn-sm" aria-label={`Télécharger ${d.numero}`} onClick={() => void telecharger(d)}><Download size={12} /></button>
              {peutEmettre && !d.annuleLe && (
                <button className="ds-btn ds-btn-g ds-btn-sm" aria-label={`Annuler ${d.numero}`} onClick={() => demanderAnnulation(d)}><X size={12} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
